import type {
  CreateMultiLegOrderRequest,
  CreateOrderRequest,
} from "~/v2/api/rest/order";
import { sideForQuoteSide } from "../../shared/optionLegs/optionLeg";
import { getIsContractSymbol } from "../../shared/symbolUtils";

export type TicketMode = "empty" | "equity" | "singleOption" | "multileg";
export type TicketSide = "buy" | "sell";
export type TicketOrderType = "market" | "limit";
export type TicketLeg = {
  contract: { symbol: string };
  quoteSide: "bid" | "ask";
};

export const deriveTicketMode = (
  symbol: string | undefined,
  legs: TicketLeg[],
): TicketMode => {
  if (legs.length >= 2) {
    return "multileg";
  }
  if (legs.length === 1) {
    return "singleOption";
  }
  if (!symbol) {
    return "empty";
  }
  return getIsContractSymbol(symbol) ? "singleOption" : "equity";
};

export const resolveOrderSymbol = (
  mode: TicketMode,
  symbol: string | undefined,
  legs: TicketLeg[],
): string | undefined =>
  mode === "singleOption"
    ? (legs[0]?.contract.symbol ?? symbol)
    : mode === "equity"
      ? symbol
      : undefined;

interface BuildTicketOrderInput {
  mode: TicketMode;
  symbol?: string;
  legs: TicketLeg[];
  side: TicketSide;
  quantity: number;
  orderType: TicketOrderType;
  limitPrice?: number;
}

export type BuiltTicketOrder =
  | { kind: "single"; request: CreateOrderRequest }
  | { kind: "multi"; request: CreateMultiLegOrderRequest };

export const buildTicketOrder = (
  input: BuildTicketOrderInput,
): BuiltTicketOrder => {
  const price =
    input.orderType === "limit" && input.limitPrice !== undefined
      ? { limit_price: String(input.limitPrice) }
      : {};

  if (input.mode === "multileg") {
    return {
      kind: "multi",
      request: {
        type: input.orderType,
        time_in_force: "day",
        qty: String(input.quantity),
        ...price,
        legs: input.legs.map((leg) => {
          const side = sideForQuoteSide(leg.quoteSide);
          return {
            symbol: leg.contract.symbol,
            side,
            ratio_qty: "1",
          };
        }),
      },
    };
  }

  const symbol = resolveOrderSymbol(input.mode, input.symbol, input.legs);
  if (!symbol || input.mode === "empty") {
    throw new Error("No symbol");
  }

  if (input.mode === "singleOption") {
    return {
      kind: "single",
      request: {
        symbol,
        side: input.side,
        type: input.orderType,
        time_in_force: "day",
        qty: String(input.quantity),
        ...price,
      },
    };
  }

  return {
    kind: "single",
    request: {
      symbol,
      side: input.side,
      type: input.orderType,
      time_in_force: "day",
      qty: String(input.quantity),
      ...price,
    },
  };
};

export const isTicketSubmittable = ({
  accountId,
  mode,
  quantity,
  orderType,
  limitPrice,
  isPending,
}: {
  accountId?: string;
  mode: TicketMode;
  quantity: number;
  orderType: TicketOrderType;
  limitPrice?: number;
  isPending: boolean;
}): boolean =>
  !!accountId &&
  quantity > 0 &&
  mode !== "empty" &&
  (orderType === "market" || (limitPrice ?? 0) > 0) &&
  !isPending;
